import { OrdwayBillingPeriod } from './ordway/plans';

/**
 * The cadence a plan's base charge recurs on.
 *
 * Only a monthly base is charged on the cycle the Overview tab shows. A longer term is invoiced
 * apart from that cycle, which then collects overages alone.
 */
export const BillingCadence = {
  Monthly: 'monthly',
  Quarterly: 'quarterly',
  SemiAnnual: 'semi-annual',
  Annual: 'annual',
  /** The cadence could not be read. Treated as monthly, so the base price stays on the estimate. */
  Unknown: 'unknown',
} as const;
export type BillingCadence = (typeof BillingCadence)[keyof typeof BillingCadence];

/** Maps an Ordway base product billing period to a cadence. */
export function ordwayCadence(billingPeriod: string | undefined): BillingCadence {
  switch (billingPeriod) {
    case OrdwayBillingPeriod.Monthly:
      return BillingCadence.Monthly;
    case OrdwayBillingPeriod.Quarterly:
      return BillingCadence.Quarterly;
    case OrdwayBillingPeriod.SemiAnnually:
      return BillingCadence.SemiAnnual;
    case OrdwayBillingPeriod.Annually:
      return BillingCadence.Annual;
    default:
      return BillingCadence.Unknown;
  }
}

/** Maps a Stripe plan interval name to a cadence. */
export function stripeCadence(intervalName: string | undefined): BillingCadence {
  switch (intervalName) {
    case 'month':
      return BillingCadence.Monthly;
    case 'quarter':
      return BillingCadence.Quarterly;
    case 'biannual':
      return BillingCadence.SemiAnnual;
    case 'year':
      return BillingCadence.Annual;
    default:
      return BillingCadence.Unknown;
  }
}

/** The base charge is invoiced apart from the monthly cycle, so that cycle carries overages alone. */
export function isNonMonthlyContract(cadence: BillingCadence): boolean {
  return (
    cadence === BillingCadence.Quarterly || cadence === BillingCadence.SemiAnnual || cadence === BillingCadence.Annual
  );
}

/** The adverb that states how often the base is billed, e.g. "billed ${label}". Null when unread. */
export function billedCadenceLabel(cadence: BillingCadence): string | null {
  switch (cadence) {
    case BillingCadence.Monthly:
      return 'monthly';
    case BillingCadence.Quarterly:
      return 'quarterly';
    case BillingCadence.SemiAnnual:
      return 'semi-annually';
    case BillingCadence.Annual:
      return 'annually';
    case BillingCadence.Unknown:
      return null;
    default: {
      const _exhaustive: never = cadence;
      return _exhaustive;
    }
  }
}
